import { ArrowLeft, Plus } from 'lucide-react';
import DocumentPreview from '../../Components/Ui/DocumentPreview';
import React, { useState } from 'react';
import Card from '../../Components/Ui/Card';
import Button from '../../Components/Ui/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toastService from '../../services/toastService';
import { useGetProductByIdQuery, useGetAllCategoriesQuery } from '../../app/services/productApi';
import {
  useUpdateCatalogItemMutation,
  useGetCatalogItemQuery,
} from '../../app/services/catalogApi';
import { useSelector } from 'react-redux';
import InputField from '../../Components/Ui/InputField';
import TierRow from '../../Components/Ui/TierRow';
import useAddProductForm from '../../hooks/useAddProductForm';
import Loader from '../../Components/Ui/Loader';

const ProductSeller = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: catalogId } = useParams();
  const productId = location.state?.product?._id || null;

  const sellerId = useSelector((state) => state?.userInfo?.user?._id);
  const {
    data: { data: product } = { data: {} },
    isLoading: productLoading,
    isError: productError,
  } = useGetProductByIdQuery(productId, { skip: !productId });
  const {
    data: catalogItem = {},
    isLoading: catalogLoading,
    isError: catalogError,
  } = useGetCatalogItemQuery(catalogId, { skip: !catalogId });
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategoriesQuery(true);
  const [updateCatalog, { isLoading: isUpdating }] = useUpdateCatalogItemMutation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [priceChangeReason, setPriceChangeReason] = useState('');

  const {
    formData,
    handleInputChange,
    tieredPricingEnabled,
    bulkPricingTiers,
    handleTieredPricingToggle,
    addTier,
    removeTier,
    updateTier,
    multiplier,
    setMultiplier,
    moqLoose,
    validateForm,
    getSubmissionData,
    categories,
    subcategories,
    keyFeatures,
    productImages,
    currentTotalMoq,
    packagingTypeDefaults,
  } = useAddProductForm(product, categoriesData || {}, catalogItem);

  const isPriceChanged =
    catalogItem &&
    (parseFloat(formData.buyingPrice || 0).toFixed(2) !==
      parseFloat(catalogItem.buyingPrice || 0).toFixed(2) ||
      parseFloat(formData.sellingPrice || 0).toFixed(2) !==
        parseFloat(catalogItem.sellerPrice || 0).toFixed(2) ||
      parseFloat(formData.discount || 0).toFixed(2) !==
        parseFloat(catalogItem.discountPercentage || 0).toFixed(2));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const submissionData = getSubmissionData(productId, sellerId);
      if (isPriceChanged && priceChangeReason) {
        submissionData.priceChangeReason = priceChangeReason;
      }
      await updateCatalog({ id: catalogId, ...submissionData }).unwrap();
      toastService.success('Catalog item updated successfully!');
      navigate('/seller/catalog');
    } catch (error) {
      toastService.error(error.data?.message || 'Failed to update catalog item.');
    }
  };

  if (productLoading || catalogLoading || categoriesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (productError || catalogError || !catalogItem) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 font-semibold">Could not load item details.</p>
        <p className="secondary-text-color mt-2">
          The product or catalog item may be missing. Please go back and try again.
        </p>
        <Button onClick={() => navigate('/seller/catalog')} className="mt-4">
          <ArrowLeft size={16} className="mr-2" /> Go to Catalog
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 lg:px-8">
        <h2 className="text-lg xl:text-2xl font-bold">Edit Catalog Product</h2>
        <Button onClick={() => navigate(-1)} className="mt-4 sm:mt-0">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
      </div>
      <Card className="mt-2 px-4 sm:px-6 py-6">
        <form className="space-y-8" onSubmit={handleSubmit}>
          {/* Section 1: Read-Only Product Info */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium tertiary-text-color">Product Information</h3>
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium secondary-text-color">Product Status</label>
                <span className="ml-2 text-sm secondary-text-color">
                  {formData.status ? 'On' : 'Off'}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={formData.status}
                    onChange={(e) =>
                      handleInputChange({
                        target: { name: 'status', value: e.target.checked },
                      })
                    }
                  />
                  <span
                    className={`w-11 h-6 flex items-center rounded-full transition-colors duration-300 ${
                      formData.status ? 'bg-[#E95B21]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                        formData.status ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <InputField
                id="name"
                name="name"
                label="Product Title"
                value={formData.name}
                disabled
                required
              />
              <InputField
                id="mrp"
                name="mrp"
                label="MRP"
                type="number"
                value={formData.mrp}
                disabled
                required
              />
              <InputField
                id="productCode"
                name="productCode"
                label="Product Code"
                value={formData.productCode}
                disabled
                required
              />
              <InputField id="sku" name="sku" label="SKU" value={formData.sku} disabled />
              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-secondary">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  disabled
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                />
              </div>
              <InputField
                id="category"
                name="category"
                label="Category"
                value={categories.find((c) => c._id === formData.category)?.name || ''}
                disabled
                required
              />
              <InputField
                id="subcategory"
                name="subcategory"
                label="Subcategory"
                value={subcategories.find((s) => s._id === formData.subcategory)?.name || ''}
                disabled
              />
            </div>
          </div>
          {/* Section 2: Seller's Catalog Details */}
          <div>
            <h3 className="text-base font-medium tertiary-text-color mb-2">Pricing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <InputField
                id="buyingPrice"
                name="buyingPrice"
                label="Cost of Goods (Per unit inclusive of tax)"
                helperText="The amount you are spending to produce/buy this product"
                type="text"
                value={formData.buyingPrice}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
              />
              <InputField
                id="sellingPrice"
                name="sellingPrice"
                label="Selling Price (Per unit inclusive of tax)"
                type="text"
                value={formData.sellingPrice}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
                required
              />
              <InputField
                id="discount"
                name="discount"
                label="Discount (%)"
                type="text"
                value={formData.discount}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
                required
              />
            </div>

            {isPriceChanged && (
              <div className="mt-4 animate-fade-in">
                <InputField
                  id="priceChangeReason"
                  name="priceChangeReason"
                  label="Reason for Price Change (Optional)"
                  placeholder="e.g., Increased raw material costs, seasonal promotion"
                  value={priceChangeReason}
                  onChange={(e) => setPriceChangeReason(e.target.value)}
                />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-end mt-6">
              <div className="lg:col-span-2">
                <label
                  htmlFor="selectedPackagingType"
                  className="block text-sm font-medium text-secondary mb-1"
                >
                  Selling Condition{' '}
                  <span className="text-xs font-normal">
                    (Select how you want to sell this product)
                  </span>{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="selectedPackagingType"
                  name="selectedPackagingType"
                  value={formData.selectedPackagingType}
                  onChange={handleInputChange}
                  required
                  className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                >
                  <option value="loose">Individual Unit (Loose packing)</option>
                  <option value="master">Master Package (Shipping carton)</option>
                  <option value="standard">Standard Package</option>
                </select>
              </div>

              {/* MOQ Calculation */}
              {formData.selectedPackagingType && (
                <div className="lg:col-span-3 grid grid-cols-4 gap-2 items-end">
                  {/* Base MOQ Value */}
                  <div className="flex flex-col col-span-1">
                    <span className="text-sm font-medium text-secondary mb-1">&nbsp;</span>
                    <div className="flex items-center">
                      <span className="p-2 border border-gray-200 bg-gray-50 rounded-md text-sm w-full text-center">
                        {formData.selectedPackagingType === 'loose'
                          ? moqLoose
                          : packagingTypeDefaults[formData.selectedPackagingType]}
                      </span>
                      <span className="text-lg font-medium ml-2">×</span>
                    </div>
                  </div>

                  <div className="flex flex-col col-span-2">
                    <label className="block text-sm font-medium text-secondary mb-1 truncate">
                      MOQ <span className="text-xs font-normal">(Smallest order size)</span>
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min={1}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        value={multiplier}
                        onChange={(e) => setMultiplier(Math.max(1, parseInt(e.target.value || 1)))}
                      />
                      <span className="text-sm secondary-text-color ml-2 whitespace-nowrap">
                        {
                          {
                            loose: 'pc/s',
                            standard: 'pkg/s',
                            master: 'carton/s',
                          }[formData.selectedPackagingType]
                        }
                      </span>
                      <span className="text-lg font-medium ml-2">=</span>
                    </div>
                  </div>

                  <div className="flex flex-col col-span-1">
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Total pieces
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        readOnly
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 text-center"
                        value={currentTotalMoq}
                      />
                      <span className="text-sm secondary-text-color ml-2">pcs</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* NEW: Tiered Pricing Section */}
          <div>
            <div className="flex items-center justify-start mb-4 gap-12">
              <h3 className="text-lg font-medium tertiary-text-color">Bulk/Tiered Pricing</h3>
              <div className="flex items-center space-x-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={tieredPricingEnabled}
                    onChange={handleTieredPricingToggle}
                  />
                  <span
                    className={`w-11 h-6 flex items-center rounded-full transition-colors duration-300 ${
                      tieredPricingEnabled ? 'bg-[#E95B21]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 tertiary-background rounded-full shadow transform transition-transform duration-300 ${
                        tieredPricingEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </span>
                </label>
                <span className="text-sm font-medium text-secondary">Enable Bulk Pricing</span>
              </div>
            </div>

            {tieredPricingEnabled && (
              <div className="space-y-4">
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-3 font-medium text-secondary">Min Quantity</th>
                        <th className="px-4 py-3 font-medium text-secondary">Unit</th>
                        <th className="px-4 py-3 font-medium text-secondary">Selling Price (₹)</th>
                        <th className="px-4 py-3 font-medium text-secondary">Discount (%)</th>
                        <th className="px-4 py-3 font-medium text-secondary">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPricingTiers.map((tier, index) => (
                        <TierRow
                          key={index}
                          tier={tier}
                          index={index}
                          onUpdate={updateTier}
                          onRemove={removeTier}
                          productMrp={parseFloat(formData.mrp)}
                          baseSellingPrice={parseFloat(formData.sellingPrice)}
                          allTiers={bulkPricingTiers}
                          canRemove={bulkPricingTiers.length > 1}
                          disableDiscount={true}
                          hideUnit={false}
                          unit={
                            {
                              loose: 'pc/s',
                              standard: 'pkg/s',
                              master: 'carton/s',
                            }[formData.selectedPackagingType]
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <Button
                  type="button"
                  onClick={addTier}
                  className="flex items-center space-x-2 text-sm px-4 py-2"
                  variant="outline"
                >
                  <Plus size={16} />
                  <span>Add Tier</span>
                </Button>

                {bulkPricingTiers.some((tier) => tier.minQuantity && tier.price) && (
                  <div className="secondary-background border border-color rounded-md p-4 mt-4">
                    <h4 className="text-sm font-medium text-primary mb-2">Pricing Preview:</h4>
                    <ul className="text-sm text-primary space-y-1">
                      <li>
                        • Base price: ₹{formData.sellingPrice} (for orders below bulk quantities)
                      </li>
                      {bulkPricingTiers
                        .filter((tier) => tier.minQuantity && tier.price)
                        .sort((a, b) => parseInt(a.minQuantity) - parseInt(b.minQuantity))
                        .map((tier, index) => (
                          <li key={index}>
                            • {tier.minQuantity}+ pieces: ₹{tier.price} ({tier.discountPercentage}%
                            off MRP)
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 4: Key Features (Read-Only Preview) */}
          <div>
            <h3 className="text-base font-medium tertiary-text-color mb-2">Key Features</h3>
            {keyFeatures.length > 0 ? (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 font-medium text-secondary">Title</th>
                      <th className="px-4 py-2 font-medium text-secondary">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keyFeatures.map((feature, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={feature.title}
                            disabled
                            className="w-full px-3 py-1 border rounded-md text-sm bg-gray-100 cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={feature.description}
                            disabled
                            className="w-full px-3 py-1 border rounded-md text-sm bg-gray-100 cursor-not-allowed"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm secondary-text-color mt-2">No key features available.</p>
            )}
          </div>

          {/* Section 3: Product Images (Read-Only Preview) */}
          <div>
            <h3 className="text-base font-medium tertiary-text-color mb-2">Product Images</h3>
            {productImages.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {productImages.map((img, i) => (
                  <div
                    key={img._id || i}
                    className="relative w-full h-32 rounded overflow-hidden border"
                  >
                    <img
                      src={img}
                      alt={product?.title ? product.title : ''}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={() => setSelectedImage(img)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm secondary-text-color mt-2">No product images available.</p>
            )}
          </div>

          <div className="flex md:justify-end space-x-4 text-sm justify-center pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="w-full sm:w-auto px-8 py-2 text-sm"
              disabled={isUpdating}
            >
              {isUpdating ? 'Saving...' : 'Update Catalog'}
            </Button>
          </div>
        </form>
        <DocumentPreview
          isOpen={!!selectedImage}
          fileUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
          fileType={selectedImage ? selectedImage.split('.').pop() : ''}
        />
      </Card>
    </div>
  );
};

export default ProductSeller;
